import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Input, InputNumber, Switch, Select, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { GenericCategoryPage } from '@/components/categories/GenericCategoryPage';
import {
  departmentService,
  taskCategoryService,
  priorityService,
  labelService,
  printTemplateService,
  roleService,
} from '@/services/categoryService';
import { userService } from '@/services/userService';
import type {
  DepartmentCategory,
  TaskCategoryItem,
  PriorityItem,
  LabelItem,
  PrintTemplateItem,
  RoleCategory,
} from '@/types/categories';
import type { User } from '@/types';

// ============================================================
// Common form fields
// ============================================================
function CommonFields({ isSystem }: { isSystem?: boolean }) {
  return (
    <>
      <Form.Item label="Mã" name="code" rules={[{ required: true, message: 'Bắt buộc' }]}>
        <Input disabled={isSystem} placeholder="VD: P_CNTT" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
      </Form.Item>
      <Form.Item label="Tên hiển thị" name="name" rules={[{ required: true }]}>
        <Input placeholder="Tên đầy đủ" />
      </Form.Item>
      <Form.Item label="Mô tả" name="description">
        <Input.TextArea rows={2} />
      </Form.Item>
      <div style={{ display: 'flex', gap: 16 }}>
        <Form.Item label="Thứ tự" name="sortOrder" style={{ flex: 1 }}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Trạng thái"
          name="isActive"
          valuePropName="checked"
          initialValue={true}
          tooltip="Tắt = ẩn khỏi combobox khi tạo task, dự án, gán tài liệu (giữ data, không xóa)"
        >
          <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
        </Form.Item>
      </div>
    </>
  );
}

function FormFooter({ onCancel }: { onCancel: () => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
      <button type="button" onClick={onCancel} style={{ padding: '6px 16px', border: '1px solid #d4d9df', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Hủy</button>
      <button type="submit" style={{ padding: '6px 16px', border: 0, borderRadius: 6, background: '#6699CC', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>Lưu</button>
    </div>
  );
}

// ============================================================
// 1. DepartmentsCategoryPage
// ============================================================
export function DepartmentsCategoryPage() {
  const [data, setData] = useState<DepartmentCategory[]>([]);
  const [groups, setGroups] = useState<any[]>([]); // V1.0.32
  // V1.0.36: Load users để chọn trưởng khoa
  const [users, setUsers] = useState<User[]>([]);
  // V1.0.44: Invalidate cache khoa ở các nơi khác sau khi sửa
  const qc = useQueryClient();

  // V1.0.44: Sau khi mutate khoa, invalidate TẤT CẢ query key liên quan
  // để các dropdown/Tree ở trang khác refetch ngay, không bị stale cache
  const invalidateAllDepartmentCaches = () => {
    qc.invalidateQueries({ queryKey: ['org-tree'] });
    qc.invalidateQueries({ queryKey: ['org-tree-public'] });
    qc.invalidateQueries({ queryKey: ['dept-groups'] });
    qc.invalidateQueries({ queryKey: ['departments'] });
    qc.invalidateQueries({ queryKey: ['departments-list'] });
    qc.invalidateQueries({ queryKey: ['org-tree-with-deleted'] });
  };

  const refresh = async () => {
    const d = await departmentService.list();
    setData(d as DepartmentCategory[]);
    invalidateAllDepartmentCaches();
  };
  useEffect(() => {
    refresh();
    // V1.0.32: Load groups để render column + Select trong form
    import('@/services/rolesService').then(m => {
      m.orgService.listGroups().then(setGroups).catch(() => {});
    });
    // V1.0.36: Load users (1 nhân viên có thể kiêm nhiệm nhiều khoa)
    userService.list({ pageSize: 500 }).then(r => setUsers(r.items ?? [])).catch(() => {});
  }, []);

  // V1.0.36: Đếm 1 user là trưởng bao nhiêu khoa — hiển thị badge "Kiêm X khoa"
  const managerCountMap = new Map<string, number>();
  for (const dept of data) {
    if (dept.managerId) {
      managerCountMap.set(dept.managerId, (managerCountMap.get(dept.managerId) ?? 0) + 1);
    }
  }

  const columns: ColumnsType<DepartmentCategory> = [
    { title: 'Mã', dataIndex: 'code', width: 110, render: (v) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    { title: 'Tên khoa/phòng', dataIndex: 'name', render: (v, r) => <><span style={{ fontWeight: 500 }}>{v}</span>{r.isSystem && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>HỆ THỐNG</Tag>}</> },
    // V1.0.32: Column Nhóm khoa phòng
    {
      title: 'Nhóm',
      dataIndex: 'groupName',
      width: 160,
      render: (v, r) => v ? (
        <Tag color={r.groupColor as any ?? 'default'} style={{ margin: 0 }}>
          {v}
        </Tag>
      ) : <span style={{ color: '#adb6c0', fontStyle: 'italic', fontSize: 11 }}>Chưa phân nhóm</span>,
      filters: [
        ...groups.map(g => ({ text: g.name, value: g.id })),
        { text: 'Chưa phân nhóm', value: '__none__' },
      ],
      onFilter: (value, record) => {
        if (value === '__none__') return !record.groupId;
        return record.groupId === value;
      },
    },
    {
      title: 'Trưởng đơn vị', dataIndex: 'managerName', width: 200,
      render: (v: string | undefined, r) => {
        if (!v || !r.managerId) return <span style={{ color: '#adb6c0' }}>—</span>;
        const count = managerCountMap.get(r.managerId) ?? 1;
        if (count > 1) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>{v}</span>
              <Tooltip title={`${v} đang là trưởng của ${count} khoa/phòng`}>
                <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>
                  Kiêm {count} khoa
                </Tag>
              </Tooltip>
            </div>
          );
        }
        return v;
      },
    },
    { title: 'Số nhân viên', dataIndex: 'userCount', width: 120, align: 'right', render: (v) => <span className="mono">{v ?? 0}</span> },
    { title: 'Thứ tự', dataIndex: 'sortOrder', width: 80, align: 'center' },
    {
      title: 'Trạng thái', dataIndex: 'isActive', width: 110,
      render: (v) => v ? <Tag color="success">● Hiện</Tag> : <Tag color="default">○ Ẩn</Tag>,
    },
  ];

  return (
    <GenericCategoryPage<DepartmentCategory>
      breadcrumb="DANH MỤC / KHOA PHÒNG"
      title="Khoa / Phòng"
      subtitle="Quản lý cơ cấu tổ chức bệnh viện. Mặc định gom theo nhóm khoa phòng — tắt bằng switch bên dưới."
      data={data}
      columns={columns}
      // V1.0.33: Gom theo nhóm khoa phòng
      groupBy={(item) => {
        if (!item.groupId) return null; // → "Chưa phân nhóm"
        return {
          key: item.groupId,
          label: item.groupName ?? '(không tên)',
          color: item.groupColor ?? undefined,
        };
      }}
      onCreate={async (v) => {
        // V1.0.65: Nếu user không chọn manager → bỏ field khỏi payload (CREATE thì null/undefined đều OK)
        await departmentService.create(v);
        refresh();
      }}
      onUpdate={async (id, v) => {
        // V1.0.65: BE convention: null/undefined = giữ nguyên, Guid.Empty = clear.
        // → Khi user bỏ trống dropdown trong UI (AntD Select allowClear → null),
        //   FE phải gửi Guid.Empty để BE biết phải clear.
        const payload = { ...v };
        if (payload.managerId == null) {
          payload.managerId = '00000000-0000-0000-0000-000000000000';
        }
        if (payload.groupId == null) {
          payload.groupId = '00000000-0000-0000-0000-000000000000';
        }
        await departmentService.update(id, payload);
        refresh();
      }}
      onDelete={async (id) => { await departmentService.delete(id); refresh(); }}
      onImport={async (file) => { const r = await departmentService.import(file); refresh(); return r; }}
      onExport={() => window.open(departmentService.exportUrl(), '_blank')}
      renderForm={({ initialValues, onFinish, isSystem }) => {
        const [form] = Form.useForm();
        return (
          <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onFinish}>
            <CommonFields isSystem={isSystem} />
            {/* V1.0.32: Field chọn Nhóm khoa phòng */}
            <Form.Item
              name="groupId"
              label="Nhóm khoa phòng"
              extra="Phân loại khoa theo nhóm. Tùy chọn — để trống nếu chưa phân nhóm."
            >
              <Select
                allowClear
                placeholder="-- Chưa phân nhóm --"
                options={groups.filter(g => g.isActive).map(g => ({
                  value: g.id,
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: g.color, display: 'inline-block',
                      }} />
                      {g.name}
                    </span>
                  ),
                }))}
              />
            </Form.Item>

            {/* V1.0.36: Chọn trưởng khoa — 1 nhân viên có thể kiêm trưởng nhiều khoa */}
            <Form.Item
              name="managerId"
              label="Trưởng đơn vị"
              extra="1 nhân viên có thể là trưởng của nhiều khoa/phòng (kiêm nhiệm). Badge tím cho thấy user đang kiêm bao nhiêu khoa."
            >
              <Select
                allowClear
                showSearch
                placeholder="-- Chưa có trưởng --"
                optionFilterProp="label"
                filterOption={(input, opt) =>
                  (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  || (opt?.title as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={users.map(u => {
                  const kiem = managerCountMap.get(u.id) ?? 0;
                  return {
                    value: u.id,
                    label: u.fullName,
                    title: u.username, // dùng để search
                    // Custom render với badge kiêm nhiệm
                    optionLabelProp: 'label',
                    children: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%' }}>
                        <span style={{ flex: 1 }}>
                          {u.fullName}
                          <span style={{ color: '#7d8a98', fontSize: 11, marginLeft: 6 }}>
                            ({u.username})
                          </span>
                        </span>
                        {kiem > 0 && (
                          <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>
                            Đang kiêm {kiem} khoa
                          </Tag>
                        )}
                      </span>
                    ),
                  };
                })}
                optionRender={(option) => (option.data as any).children ?? option.label}
              />
            </Form.Item>

            <FormFooter onCancel={() => form.resetFields()} />
          </Form>
        );
      }}
    />
  );
}

// ============================================================
// 2. TaskCategoriesCategoryPage
// ============================================================
export function TaskCategoriesCategoryPage() {
  const [data, setData] = useState<TaskCategoryItem[]>([]);
  const refresh = () => taskCategoryService.list().then((d) => setData(d as TaskCategoryItem[]));
  useEffect(() => { refresh(); }, []);

  const columns: ColumnsType<TaskCategoryItem> = [
    { title: 'Mã', dataIndex: 'code', width: 120, render: (v) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    {
      title: 'Tên loại', dataIndex: 'name', render: (v, r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: r.color }} />
          <span style={{ fontWeight: 500 }}>{v}</span>
          {r.isSystem && <Tag color="blue" style={{ fontSize: 10 }}>HỆ THỐNG</Tag>}
        </span>
      ),
    },
    { title: 'Mô tả', dataIndex: 'description', render: (v) => <span style={{ color: '#5b6875' }}>{v}</span> },
    { title: 'Màu', dataIndex: 'color', width: 100, render: (v) => <code style={{ fontSize: 11 }}>{v}</code> },
    { title: 'Thứ tự', dataIndex: 'sortOrder', width: 80, align: 'center' },
    { title: 'Trạng thái', dataIndex: 'isActive', width: 110, render: (v) => v ? <Tag color="success">● Active</Tag> : <Tag>○ Inactive</Tag> },
  ];

  return (
    <GenericCategoryPage<TaskCategoryItem>
      breadcrumb="DANH MỤC / LOẠI CÔNG VIỆC"
      title="Loại công việc"
      subtitle="Phân loại task theo nghiệp vụ"
      data={data}
      columns={columns}
      onCreate={async (v) => { await taskCategoryService.create(v); refresh(); }}
      onUpdate={async (id, v) => { await taskCategoryService.update(id, v); refresh(); }}
      onDelete={async (id) => { await taskCategoryService.delete(id); refresh(); }}
      onImport={async (file) => { const r = await taskCategoryService.import(file); refresh(); return r; }}
      onExport={() => window.open(taskCategoryService.exportUrl(), '_blank')}
      renderForm={({ initialValues, onFinish, isSystem }) => {
        const [form] = Form.useForm();
        return (
          <Form form={form} layout="vertical" initialValues={{ color: '#6699CC', ...initialValues }} onFinish={onFinish}>
            <CommonFields isSystem={isSystem} />
            <Form.Item label="Màu hiển thị" name="color" rules={[{ required: true }]}>
              <Input type="color" style={{ width: 80, height: 32, padding: 2 }} />
            </Form.Item>
            <FormFooter onCancel={() => form.resetFields()} />
          </Form>
        );
      }}
    />
  );
}

// ============================================================
// 3. PrioritiesCategoryPage
// ============================================================
export function PrioritiesCategoryPage() {
  const [data, setData] = useState<PriorityItem[]>([]);
  const refresh = () => priorityService.list().then((d) => setData(d as PriorityItem[]));
  useEffect(() => { refresh(); }, []);

  const columns: ColumnsType<PriorityItem> = [
    { title: 'Mã', dataIndex: 'code', width: 110, render: (v) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    {
      title: 'Tên', dataIndex: 'name', render: (v, r) => (
        <Tag color={r.color === '#991b1b' ? 'error' : r.color === '#dc2626' ? 'red' : r.color === '#d97706' ? 'warning' : 'default'}>
          {v}
        </Tag>
      ),
    },
    { title: 'Mức độ', dataIndex: 'level', width: 100, align: 'center', render: (v) => <span className="mono" style={{ fontWeight: 600 }}>{v}/10</span> },
    { title: 'SLA (giờ)', dataIndex: 'slaHours', width: 110, align: 'right', render: (v) => v ? <span className="mono">{v}h</span> : '—' },
    { title: 'Thứ tự', dataIndex: 'sortOrder', width: 80, align: 'center' },
    { title: 'Trạng thái', dataIndex: 'isActive', width: 110, render: (v) => v ? <Tag color="success">● Active</Tag> : <Tag>○ Inactive</Tag> },
  ];

  return (
    <GenericCategoryPage<PriorityItem>
      breadcrumb="DANH MỤC / ĐỘ ƯU TIÊN"
      title="Độ ưu tiên"
      subtitle="Cấp độ ưu tiên task và SLA mặc định"
      data={data}
      columns={columns}
      onCreate={async (v) => { await priorityService.create(v); refresh(); }}
      onUpdate={async (id, v) => { await priorityService.update(id, v); refresh(); }}
      onDelete={async (id) => { await priorityService.delete(id); refresh(); }}
      onImport={async (file) => { const r = await priorityService.import(file); refresh(); return r; }}
      onExport={() => window.open(priorityService.exportUrl(), '_blank')}
      renderForm={({ initialValues, onFinish, isSystem }) => {
        const [form] = Form.useForm();
        return (
          <Form form={form} layout="vertical" initialValues={{ color: '#d97706', level: 5, ...initialValues }} onFinish={onFinish}>
            <CommonFields isSystem={isSystem} />
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item label="Mức độ (1-10)" name="level" rules={[{ required: true }]} style={{ flex: 1 }}>
                <InputNumber min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="SLA (giờ)" name="slaHours" style={{ flex: 1 }}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item label="Màu" name="color">
              <Input type="color" style={{ width: 80, height: 32, padding: 2 }} />
            </Form.Item>
            <FormFooter onCancel={() => form.resetFields()} />
          </Form>
        );
      }}
    />
  );
}

// ============================================================
// 4. LabelsCategoryPage
// ============================================================
export function LabelsCategoryPage() {
  const [data, setData] = useState<LabelItem[]>([]);
  const refresh = () => labelService.list().then((d) => setData(d as LabelItem[]));
  useEffect(() => { refresh(); }, []);

  const columns: ColumnsType<LabelItem> = [
    { title: 'Mã', dataIndex: 'code', width: 130, render: (v) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    {
      title: 'Nhãn', dataIndex: 'name', render: (v, r) => (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, background: r.color + '22', color: r.color, fontSize: 12, fontWeight: 500 }}>
          {v}
        </span>
      ),
    },
    { title: 'Màu', dataIndex: 'color', width: 100, render: (v) => <code style={{ fontSize: 11 }}>{v}</code> },
    { title: 'Lượt dùng', dataIndex: 'usageCount', width: 120, align: 'right', render: (v) => <span className="mono">{v ?? 0}</span> },
    { title: 'Thứ tự', dataIndex: 'sortOrder', width: 80, align: 'center' },
    { title: 'Trạng thái', dataIndex: 'isActive', width: 110, render: (v) => v ? <Tag color="success">● Active</Tag> : <Tag>○ Inactive</Tag> },
  ];

  return (
    <GenericCategoryPage<LabelItem>
      breadcrumb="DANH MỤC / NHÃN"
      title="Nhãn / Tag"
      subtitle="Gắn nhãn tùy biến cho task (#SIEM, #Q2-2026...)"
      data={data}
      columns={columns}
      onCreate={async (v) => { await labelService.create(v); refresh(); }}
      onUpdate={async (id, v) => { await labelService.update(id, v); refresh(); }}
      onDelete={async (id) => { await labelService.delete(id); refresh(); }}
      onImport={async (file) => { const r = await labelService.import(file); refresh(); return r; }}
      onExport={() => window.open(labelService.exportUrl(), '_blank')}
      renderForm={({ initialValues, onFinish, isSystem }) => {
        const [form] = Form.useForm();
        return (
          <Form form={form} layout="vertical" initialValues={{ color: '#6699CC', ...initialValues }} onFinish={onFinish}>
            <CommonFields isSystem={isSystem} />
            <Form.Item label="Màu" name="color">
              <Input type="color" style={{ width: 80, height: 32, padding: 2 }} />
            </Form.Item>
            <FormFooter onCancel={() => form.resetFields()} />
          </Form>
        );
      }}
    />
  );
}

// ============================================================
// 5. PrintTemplatesCategoryPage
// ============================================================
export function PrintTemplatesCategoryPage() {
  const [data, setData] = useState<PrintTemplateItem[]>([]);
  const refresh = () => printTemplateService.list().then((d) => setData(d as PrintTemplateItem[]));
  useEffect(() => { refresh(); }, []);

  const TYPE_LABELS: Record<string, string> = {
    TASK_ASSIGNMENT: 'Phiếu giao việc',
    TASK_RESULT: 'Báo cáo kết quả',
    REPORT: 'Báo cáo',
    OTHER: 'Khác',
  };
  const TYPE_COLORS: Record<string, string> = {
    TASK_ASSIGNMENT: 'blue', TASK_RESULT: 'green', REPORT: 'purple', OTHER: 'default',
  };

  const columns: ColumnsType<PrintTemplateItem> = [
    { title: 'Mã', dataIndex: 'code', width: 140, render: (v) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    { title: 'Tên mẫu', dataIndex: 'name', render: (v, r) => <><span style={{ fontWeight: 500 }}>{v}</span>{r.isSystem && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>HỆ THỐNG</Tag>}</> },
    { title: 'Loại', dataIndex: 'templateType', width: 160, render: (v) => <Tag color={TYPE_COLORS[v]}>{TYPE_LABELS[v] ?? v}</Tag> },
    { title: 'Giấy', dataIndex: 'paperSize', width: 80 },
    { title: 'Hướng', dataIndex: 'orientation', width: 100, render: (v) => v === 'PORTRAIT' ? 'Dọc' : 'Ngang' },
    { title: 'Phiên bản', dataIndex: 'version', width: 100, align: 'center', render: (v) => <span className="mono">v{v}</span> },
    { title: 'Trạng thái', dataIndex: 'isActive', width: 110, render: (v) => v ? <Tag color="success">● Active</Tag> : <Tag>○ Inactive</Tag> },
  ];

  return (
    <GenericCategoryPage<PrintTemplateItem>
      breadcrumb="DANH MỤC / MẪU BIỂU IN"
      title="Mẫu biểu in"
      subtitle="Quản lý template phiếu giao việc, báo cáo, biểu mẫu"
      data={data}
      columns={columns}
      onCreate={async (v) => { await printTemplateService.create(v); refresh(); }}
      onUpdate={async (id, v) => { await printTemplateService.update(id, v); refresh(); }}
      onDelete={async (id) => { await printTemplateService.delete(id); refresh(); }}
      renderForm={({ initialValues, onFinish, isSystem }) => {
        const [form] = Form.useForm();
        return (
          <Form form={form} layout="vertical" initialValues={{ paperSize: 'A4', orientation: 'PORTRAIT', version: 1, templateType: 'OTHER', ...initialValues }} onFinish={onFinish}>
            <CommonFields isSystem={isSystem} />
            <Form.Item label="Loại mẫu" name="templateType" rules={[{ required: true }]}>
              <Select
                disabled={isSystem}
                options={[
                  { value: 'TASK_ASSIGNMENT', label: 'Phiếu giao việc' },
                  { value: 'TASK_RESULT', label: 'Báo cáo kết quả' },
                  { value: 'REPORT', label: 'Báo cáo' },
                  { value: 'OTHER', label: 'Khác' },
                ]}
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item label="Khổ giấy" name="paperSize" style={{ flex: 1 }}>
                <Select options={[{ value: 'A4' }, { value: 'A5' }, { value: 'LETTER' }]} />
              </Form.Item>
              <Form.Item label="Hướng" name="orientation" style={{ flex: 1 }}>
                <Select options={[{ value: 'PORTRAIT', label: 'Dọc' }, { value: 'LANDSCAPE', label: 'Ngang' }]} />
              </Form.Item>
              <Form.Item label="Version" name="version" style={{ flex: 1 }}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <FormFooter onCancel={() => form.resetFields()} />
          </Form>
        );
      }}
    />
  );
}

// ============================================================
// 6. RolesCategoryPage
// ============================================================
export function RolesCategoryPage() {
  const [data, setData] = useState<RoleCategory[]>([]);
  const refresh = () => roleService.list().then((d) => setData(d as RoleCategory[]));
  useEffect(() => { refresh(); }, []);

  const columns: ColumnsType<RoleCategory> = [
    { title: 'Mã', dataIndex: 'code', width: 130, render: (v) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    { title: 'Tên vai trò', dataIndex: 'name', render: (v, r) => <><span style={{ fontWeight: 500 }}>{v}</span>{r.isSystem && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>HỆ THỐNG</Tag>}</> },
    { title: 'Mô tả', dataIndex: 'description', render: (v) => <span style={{ color: '#5b6875' }}>{v}</span> },
    { title: 'Số quyền', dataIndex: 'permissionCount', width: 110, align: 'right', render: (v) => <span className="mono">{v ?? 0}</span> },
    { title: 'Số user', dataIndex: 'userCount', width: 110, align: 'right', render: (v) => <span className="mono">{v ?? 0}</span> },
    {
      title: 'Trạng thái', dataIndex: 'isActive', width: 110,
      render: (v) => v ? <Tag color="success">● Hiện</Tag> : <Tag color="default">○ Ẩn</Tag>,
    },
  ];

  return (
    <GenericCategoryPage<RoleCategory>
      breadcrumb="DANH MỤC / VAI TRÒ"
      title="Vai trò & Phân quyền"
      subtitle="Role-based access control (RBAC)"
      data={data}
      columns={columns}
      onCreate={async (v) => { await roleService.create(v); refresh(); }}
      onUpdate={async (id, v) => { await roleService.update(id, v); refresh(); }}
      onDelete={async (id) => { await roleService.delete(id); refresh(); }}
      onImport={async (file) => { const r = await roleService.import(file); refresh(); return r; }}
      onExport={() => window.open(roleService.exportUrl(), '_blank')}
      renderForm={({ initialValues, onFinish, isSystem }) => {
        const [form] = Form.useForm();
        return (
          <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onFinish}>
            <CommonFields isSystem={isSystem} />
            <FormFooter onCancel={() => form.resetFields()} />
          </Form>
        );
      }}
    />
  );
}
